import React, { useCallback, useEffect, useRef, useState } from 'react';
import { OpeningAllocationReversal } from '../components/OpeningAllocationReversal';
import { Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, Table, TableBody, TableCell, TableHead, TableRow, TablePagination, TextField, Typography } from '@mui/material';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import { Spinner } from 'react-bootstrap';
import { resourceCache } from 'use-async-resource';
import { useGetAllDocuments, useGetAllTurnovers } from '../utils/users-hooks';
import { useGetInvoices } from '../utils/clients-hooks';
import { OpeningBalance, OpeningInvoiceTarget, OpeningSettlement, OpeningSettlementRequest, OpeningImport, OpeningImportResult } from '../models/opening-balances';
import { allocateOpening, importOpening, openingBalance, openingBalances, openingHistory, openingRequestId, openingTargets, payOpening } from '../utils/opening-balances-api';

const money = (cents: number) => `${(cents / 100).toFixed(2)} EUR`;
const today = () => new Intl.DateTimeFormat('sv-SE', { timeZone: 'Europe/Sofia' }).format(new Date());
type Values = { amount: string; date: string; method: 'BANK' | 'CASH' | 'CARD'; target: string; comment: string };
const schema = yup.object({
    amount: yup.string().required('amountRequired').matches(/^\d+([.,]\d{1,2})?$/, 'amountFormat'),
    date: yup.string().required('dateRequired'), comment: yup.string().max(2000, 'commentLength'),
});

export default function OpeningBalances({ balanceId, onClose, onChanged }: {
    balanceId?: number; onClose?: () => void; onChanged?: () => void;
} = {}) {
    const { t } = useTranslation('translation', { keyPrefix: 'openingBalances' });
    const { t: common } = useTranslation('translation', { keyPrefix: 'common' });
    const [params] = useSearchParams();
    const clientId = Number(params.get('clientId')) || undefined;
    const [rows, setRows] = useState<OpeningBalance[]>([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(0);
    const [search, setSearch] = useState('');
    const [kind, setKind] = useState('');
    const [status, setStatus] = useState('open');
    const [busy, setBusy] = useState(true);
    const [error, setError] = useState('');
    const [selected, setSelected] = useState<OpeningBalance>();
    const [history, setHistory] = useState<OpeningSettlement[]>([]);
    const [debts, setDebts] = useState<OpeningBalance[]>([]);
    const [invoices, setInvoices] = useState<OpeningInvoiceTarget[]>([]);
    const [ready, setReady] = useState(false);
    const [saving, setSaving] = useState(false);
    const [reversalOpen, setReversalOpen] = useState(false);
    const retry = useRef<{ signature: string; requestId: string }>();
    const listRequest = useRef(0);
    const dialogRequest = useRef(0);
    const submitting = useRef(false);
    const [importData, setImportData] = useState<OpeningImport>();
    const [preview, setPreview] = useState<OpeningImportResult>();
    const { register, handleSubmit, reset, formState: { errors } } = useForm<Values>({ resolver: async values => {
        try { await schema.validate(values, { abortEarly: false }); return { values, errors: {} }; }
        catch (error) {
            if (!(error instanceof yup.ValidationError)) throw error;
            return { values: {}, errors: error.inner.reduce((all, item) => ({ ...all,
                [item.path || 'amount']: { type: item.type || 'validation', message: item.message } }), {}) };
        }
    } });
    const load = useCallback(async () => {
        if (balanceId) return;
        const request = ++listRequest.current;
        setBusy(true); setError('');
        try {
            const result = await openingBalances({ clientId, search, kind: kind || undefined, open: status === 'all' ? undefined : status === 'open', page, size: 50 });
            if (request !== listRequest.current) return;
            setRows(result.content); setTotal(result.totalElements);
        } catch { if (request === listRequest.current) setError('loadError'); }
        finally { if (request === listRequest.current) setBusy(false); }
    }, [clientId, search, kind, status, page, balanceId]);
    useEffect(() => { void load(); return () => { ++listRequest.current; }; }, [load]);
    const close = () => { if (!submitting.current) { ++dialogRequest.current; setSelected(undefined); setError(''); onClose?.(); } };
    const open = async (row: OpeningBalance) => {
        const request = ++dialogRequest.current;
        setSelected(row); setHistory([]); setDebts([]); setInvoices([]); setReady(false); setError(''); retry.current = undefined;
        reset({ amount: (row.remaining / 100).toFixed(2), date: today(), method: 'BANK', target: '', comment: '' });
        try {
            const history = await openingHistory(row.id);
            if (request !== dialogRequest.current) return;
            setHistory(history);
            if (row.kind === 'CREDIT') {
                const all: OpeningBalance[] = [];
                let p = 0, pages = 1;
                while (p < pages) {
                    const result = await openingBalances({ clientId: row.clientId, kind: 'DEBT', open: true, size: 200, page: p++ });
                    if (request !== dialogRequest.current) return;
                    all.push(...result.content); pages = result.totalPages;
                }
                const targets = await openingTargets(row.clientId);
                if (request !== dialogRequest.current) return;
                setDebts(all); setInvoices(targets);
            }
            setReady(true);
        } catch { if (request === dialogRequest.current) setError('historyError'); }
    };
    useEffect(() => {
        if (!balanceId) return;
        let active = true;
        openingBalance(balanceId).then(row => { if (active) void open(row); })
            .catch(() => { if (active) setError('loadError'); });
        return () => { active = false; ++dialogRequest.current; };
    }, [balanceId]);
    const submit = handleSubmit(async values => {
        if (!selected || submitting.current) return;
        const amount = Math.round(Number(values.amount.replace(',', '.')) * 100);
        if (!Number.isSafeInteger(amount) || amount <= 0 || amount > selected.remaining) { setError('invalidAmount'); return; }
        if (values.date < selected.asOfDate || values.date > today()) { setError('invalidDate'); return; }
        const request: OpeningSettlementRequest = { requestId: '', amount, paymentDate: values.date, comment: values.comment };
        if (selected.kind === 'DEBT') request.paymentMethod = values.method;
        else {
            const [type, id] = values.target.split(':');
            const target = type === 'debt' ? debts.find(d => d.id === Number(id)) : invoices.find(i => i.documentId === Number(id));
            if (!target || amount > target.remaining) { setError('invalidTarget'); return; }
            if (type === 'debt') request.debtId = Number(id); else request.documentId = Number(id);
        }
        const signature = JSON.stringify(request);
        if (retry.current?.signature !== signature) retry.current = { signature, requestId: openingRequestId() };
        request.requestId = retry.current.requestId;
        submitting.current = true; setSaving(true); setError('');
        try {
            await (selected.kind === 'DEBT' ? payOpening(selected.id, request) : allocateOpening(selected.id, request));
            resourceCache(useGetAllTurnovers).clear();
            resourceCache(useGetInvoices).clear();
            resourceCache(useGetAllDocuments).clear();
            setSelected(undefined); await load();
            onChanged?.(); onClose?.();
        } catch { setError('saveError'); }
        finally { submitting.current = false; setSaving(false); }
    });
    const readImport = async (file?: File) => {
        setPreview(undefined); setImportData(undefined); setError('');
        if (!file) return;
        setBusy(true);
        try { const data: OpeningImport = JSON.parse(await file.text()); setPreview(await importOpening(data, true)); setImportData(data); }
        catch { setError('previewError'); }
        finally { setBusy(false); }
    };
    const commitImport = async () => {
        if (!importData || !preview) return;
        setBusy(true);
        try {
            await importOpening(importData, false);
            resourceCache(useGetInvoices).clear();
            setImportData(undefined); setPreview(undefined); await load();
        }
        catch { setError('importError'); }
        finally { setBusy(false); }
    };
    return <Box sx={{ p: 3 }}>
        {!balanceId && <><Typography variant='h5' gutterBottom>{t('title')}</Typography>
        <Typography sx={{ mb: 2 }}>{t('description')}</Typography>
        {error && <Alert severity='error' sx={{ mb: 2 }}>{t(error)}</Alert>}
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
            <TextField label={t('search')} value={search} onChange={e => { setSearch(e.target.value); setPage(0); }} />
            <TextField select label={common('type')} value={kind} onChange={e => { setKind(e.target.value); setPage(0); }} sx={{ minWidth: 180 }}>
                <MenuItem value=''>{common('all')}</MenuItem><MenuItem value='DEBT'>{t('debts')}</MenuItem><MenuItem value='CREDIT'>{t('credits')}</MenuItem>
            </TextField>
            <TextField select label={common('state')} value={status} onChange={e => { setStatus(e.target.value); setPage(0); }} sx={{ minWidth: 180 }}>
                <MenuItem value='open'>{t('open')}</MenuItem><MenuItem value='closed'>{t('closed')}</MenuItem><MenuItem value='all'>{common('all')}</MenuItem>
            </TextField>
            <Button disabled={busy} onClick={load}>{t('reload')}</Button>
            <Button component='label' disabled={busy}>{t('previewImport')}<input hidden type='file' accept='.json' onChange={e => { void readImport(e.target.files?.[0]); e.target.value = ''; }} /></Button>
        </Box>
        {preview && <Alert severity='info' sx={{ mb: 2 }}>{t('previewSummary', { newRows: preview.newRows, existingRows: preview.existingRows, debts: money(preview.debtCents), credits: money(preview.creditCents) })}
            <Button disabled={busy} onClick={commitImport}>{t('commitImport')}</Button>
        </Alert>}
        <Box aria-busy={busy} sx={{ overflowX: 'auto', opacity: busy ? 0.5 : 1 }}><Table size='small' sx={{ tableLayout: 'fixed', minWidth: 1100, '& th, & td': { overflowWrap: 'anywhere' } }}>
            <colgroup>
                {[18, 16, 18, 14, 8, 8, 8, 10].map((width, index) => <col key={index} style={{ width: `${width}%` }} />)}
            </colgroup>
            <TableHead><TableRow>{['clientEik', 'branchRegional', 'kindInvoice', 'dateAsOf', 'initial', 'settled', 'remaining', 'actions'].map(h => <TableCell key={h}>{t(h)}</TableCell>)}</TableRow></TableHead>
            <TableBody>{busy ? <TableRow><TableCell colSpan={8}>
                <div className='d-flex justify-content-center w-100 py-3' role='status' aria-busy='true'><Spinner animation='border' /></div>
            </TableCell></TableRow> : rows.map(row => <TableRow key={row.id}>
                <TableCell>{row.clientName}<br />{row.eik}</TableCell><TableCell>{row.branchName}<br />{row.regional}</TableCell>
                <TableCell>{row.kind === 'DEBT' ? t('debtNumber', { number: row.invoiceNumber }) : t('credit')}</TableCell>
                <TableCell>{row.invoiceDate}<br />{t('asOf', { date: row.asOfDate })}</TableCell>
                <TableCell>{money(row.amount)}</TableCell><TableCell>{money(row.settledAmount)}</TableCell><TableCell>{money(row.remaining)}</TableCell>
                <TableCell><Button disabled={busy} onClick={() => open(row)}>{t('view')}</Button></TableCell>
            </TableRow>)}</TableBody>
        </Table></Box>
        {!busy && !rows.length && <Typography sx={{ p: 2 }}>{t('empty')}</Typography>}
        <TablePagination component='div' count={total} page={page} rowsPerPage={50} rowsPerPageOptions={[50]} onPageChange={(_, p) => setPage(p)}
            labelRowsPerPage={common('numberOfRows')} labelDisplayedRows={({ from, to, count }) => t('pageRange', { from, to, total: count })}
            getItemAriaLabel={type => t(`page.${type}`)} /></>}
        {balanceId && error && !selected && <Alert severity='error'>{t(error)}<Button onClick={onClose}>{common('close')}</Button></Alert>}
        <Dialog open={!!selected} onClose={() => !reversalOpen && close()} disableEnforceFocus={reversalOpen} fullWidth maxWidth='md'>
            <DialogTitle>{selected?.clientName} — {selected?.kind === 'DEBT' ? t('invoiceNumber', { number: selected.invoiceNumber }) : t('credit')}</DialogTitle>
            <DialogContent>
                {error && <Alert severity='error'>{t(error)}</Alert>}
                <Typography>{t('sourceSummary', { remaining: money(selected?.remaining || 0), sheet: selected?.sourceSheet, row: selected?.sourceRow, amount: selected?.sourceAmount, currency: selected?.sourceCurrency })}</Typography>
                <Typography>{selected?.comment}</Typography>
                {((!ready && !error) || saving) && <div className='d-flex justify-content-center w-100 py-3' role='status' aria-busy='true'><Spinner animation='border' /></div>}
                <Table size='small'><TableHead><TableRow><TableCell>{common('date')}</TableCell><TableCell>{t('operationTarget')}</TableCell><TableCell>{t('amount')}</TableCell><TableCell>{t('operatorId')}</TableCell><TableCell>{common('comment')}</TableCell></TableRow></TableHead>
                    <TableBody>{history.map(s => <TableRow key={s.id}><TableCell>{s.paymentDate}</TableCell><TableCell>{s.creditId ? t('allocation') : common((s.paymentMethod || '').toLowerCase())}{s.documentId ? ` / ${t('documentId', { id: s.documentId })}` : s.debtId ? ` / ${t('balanceId', { id: s.debtId })}` : ''}</TableCell><TableCell>{money(s.amount)}</TableCell><TableCell>{s.recordedBy}</TableCell><TableCell>{s.comment}<OpeningAllocationReversal settlement={s} onDialogChange={setReversalOpen} onReversed={() => { onChanged?.(); close(); void load(); }} /></TableCell></TableRow>)}</TableBody>
                </Table>
                {!!selected?.remaining && ready && <Box component='form' id='opening-settlement' onSubmit={submit} sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 3 }}>
                    {selected.kind === 'CREDIT' ? <TextField select label={t('allocateTo')} defaultValue='' {...register('target')}>
                        {debts.map(d => <MenuItem key={`d${d.id}`} value={`debt:${d.id}`}>{t('openingTarget', { number: d.invoiceNumber, remaining: money(d.remaining) })}</MenuItem>)}
                        {invoices.map(i => <MenuItem key={`i${i.documentId}`} value={`document:${i.documentId}`}>{t('currentTarget', { number: i.invoiceNumber, remaining: money(i.remaining) })}</MenuItem>)}
                    </TextField> : <TextField select label={common('paymentMethod')} defaultValue='BANK' {...register('method')}><MenuItem value='BANK'>{common('bank')}</MenuItem><MenuItem value='CASH'>{common('cash')}</MenuItem><MenuItem value='CARD'>{common('card')}</MenuItem></TextField>}
                    <TextField label={t('amountEUR')} {...register('amount')} error={!!errors.amount} helperText={errors.amount?.message && t(errors.amount.message)} />
                    <TextField label={common('date')} type='date' InputLabelProps={{ shrink: true }} inputProps={{ min: selected.asOfDate, max: today() }} {...register('date')} error={!!errors.date} helperText={errors.date?.message && t(errors.date.message)} />
                    <TextField label={common('comment')} {...register('comment')} error={!!errors.comment} helperText={errors.comment?.message && t(errors.comment.message)} />
                </Box>}
            </DialogContent>
            <DialogActions><Button disabled={saving} onClick={close}>{common('close')}</Button>
                {!!selected?.remaining && <Button form='opening-settlement' type='submit' disabled={saving || !ready}>{t(selected.kind === 'DEBT' ? 'savePayment' : 'allocateCredit')}</Button>}
            </DialogActions>
        </Dialog>
    </Box>;
}
